import React, { useEffect, useRef, useState, useMemo } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { formatPriceMejorado } from '../../../../_mexablock/helpers';
import { getStudentAge } from '../../../service/dashboard/Dashboard';

type Props = {
  filterDate: any;
};

const GraficaBalance: React.FC<Props> = ({ filterDate }) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [seriesData, setSeriesData] = useState<any[]>([]);
  const [indicatorTotalData, setIndicatorTotalData] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<number>(1);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<ApexCharts | null>(null);
  const prevFiltersRef = useRef<string>();
  const fecha = new Date();
  const mes = fecha.toLocaleDateString('es-ES', { month: 'long' });
  const año = fecha.getFullYear();
  const mesYAnio = `${mes.charAt(0).toUpperCase() + mes.slice(1)} ${año}`;

  // Usar useMemo para estabilizar el objeto de filtros
  const filters = useMemo(() => ({
    init_date: filterDate.init_date,
    end_date: filterDate.end_date,
  }), [filterDate.init_date, filterDate.end_date]);

  const fetchPost = async () => {
    try {
      const sends = await getStudentAge(filters);

      if (sends) {
        const categoriesMapped = sends.ranges.map((r: any) => r.range);
        const seriesMapped = [
          {
            name: 'Usuarios',
            data: sends.ranges.map((r: any) => r.count),
          },
        ];

        setCategories(categoriesMapped);
        setSeriesData(seriesMapped);
        setIndicatorTotalData(sends.total);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const filtersString = JSON.stringify(filters);
    
    // Evitar llamadas duplicadas
    if (prevFiltersRef.current === filtersString) return;
    prevFiltersRef.current = filtersString;
    
    if (filterDate) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!chartRef.current) return;

    const height = parseInt(window.getComputedStyle(chartRef.current).height);
    const viewType =
      statusFilter === 1 ? 'year' : statusFilter === 2 ? 'month' : 'week';

    if (!chartInstance.current) {
      // Crear la gráfica una sola vez
      chartInstance.current = new ApexCharts(
        chartRef.current,
        getChartOptions(height, seriesData, categories)
      );
      chartInstance.current.render();
    } else {
      // Actualizar datos cada vez que cambien
      chartInstance.current.updateOptions(
        getChartOptions(height, seriesData, categories),
        true,
        true
      );
    }
  }, [seriesData, categories, statusFilter]);

  // Destruir solo al desmontar el componente
  useEffect(() => {
    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, []);

  return (
    <div className="card">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">
            Registro de usuarios por rango de edad
          </span>
          <span className="text-muted fw-semibold fs-7">{mesYAnio}</span>
        </h3>
      </div>
      {/* Indicadores */}
      <div className="row w-75 ms-9 mt-5">
        <div className="col-3">
          <div>
            <span className="me-1 w-100" style={{ backgroundColor: '#EAF8FF' }}>
              <i className="fas fa-users" style={{ color: '#F1416c' }} />
            </span>
            <span className="text-gray-600 fw-bold fs-5">
              Total de usuarios
            </span>
          </div>
          <div className="ms-5">
            <span className="fw-bolder fs-4">
              {typeof indicatorTotalData === 'number' ? indicatorTotalData : 0}
            </span>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div style={{ overflowX: 'auto' }}>
          {seriesData.length === 0 || categories.length === 0 ? (
            <div className="text-muted text-center py-5">
              Cargando gráfica...
            </div>
          ) : (
            <div
              ref={chartRef}
              id="kt_charts_widget_2_chart"
              style={{ height: '350px', minWidth: '1000px' }}
            ></div>
          )}
        </div>
      </div>
    </div>
  );
};

export { GraficaBalance };

function getChartOptions(
  height: number,
  series: any[],
  categories: string[]
): ApexOptions {
  const labelColor = '#6c757d';
  const borderColor = '#e9ecef';

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height,
      // width: 1000,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '7px',
        borderRadius: 4,
      },
    },
    colors: ['#F1416c'],
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 1,
      colors: ['transparent'],
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: labelColor, fontSize: '12px' },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val.toLocaleString('es-MX', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        style: { colors: labelColor, fontSize: '12px' },
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toLocaleString('es-MX')} usuarios`,
      },
    },
    grid: {
      borderColor,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
  };
}
